import ErrorPage from 'next/error';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { ClockIcon } from '@heroicons/react/outline';
import {
  tutorialQuery,
  tutorialSlugsQueryPreview,
  tutorialSlugsQueryProduction
} from '@lib/queries';
import { usePreviewSubscription } from '@lib/sanity';
import singlePageProps from '@lib/singlePageProps';
import { getClient, overlayDrafts, sanityClient } from '@lib/sanity.server';
import { isProduction } from 'utils';
import { PreviewBadge } from '@components/blog';
import { SaneImage } from '@components/sanity/sane-image';
import { URLS } from '@lib';
import { SaneBlockChakra } from '@components';

const { tutorials } = URLS;

export default function TutorialPage({ data = {}, preview }: any) {
  const router = useRouter();

  const slug = data?.tutorial?.slug;
  const live = data?.tutorial?.live;
  const {
    data: { tutorial }
  } = usePreviewSubscription(tutorialQuery, {
    params: { slug },
    initialData: data,
    enabled: preview && slug
  });

  if ((!router.isFallback && !slug) || (isProduction && !live)) {
    return <ErrorPage statusCode={404} />;
  }

  return router.isFallback ? (
    <div>Loading</div>
  ) : (
    <div className="relative py-16 overflow-hidden">
      <div className="hidden lg:block lg:absolute lg:inset-y-0 lg:h-full lg:w-full">
        <div
          className="relative h-full mx-auto text-lg max-w-prose"
          aria-hidden="true"
        ></div>
      </div>
      <PreviewBadge isLive={tutorial.live} isTop />
      <article className="relative px-4 clerk-tutorial sm:px-6 lg:px-8">
        <div className="mx-auto text-lg max-w-prose">
          {tutorial.coverImage && (
            <SaneImage width={700} src={tutorial.coverImage} />
          )}
          <div className="block text-base font-semibold tracking-wide text-gray-600 uppercase mt-14">
            <Link legacyBehavior href={tutorials}>
              <a>Tutorials</a>
            </Link>
            {` »  ${tutorial.category}`}
          </div>
          <h1 className="block mt-2 text-3xl font-extrabold leading-8 tracking-tight text-gray-900 sm:text-4xl">
            {tutorial.title}
          </h1>
          <h3 className="block mt-2 text-sm font-semibold leading-8 tracking-tight text-gray-500 text sm:text-1xl">
            <ClockIcon className="inline w-6 h-6" />{' '}
            <span>Estimated time:</span> {tutorial.time}
          </h3>
          <div className="flex items-center mt-6">
            <div className="flex-shrink-0">
              <span className="sr-only">{tutorial.author.name}</span>
              <SaneImage
                className="block w-10 h-10 rounded-full"
                src={tutorial.author.picture}
                height={40}
                width={40}
                quality={100}
              />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-900">
                {tutorial.author.name}
              </p>
            </div>
          </div>
        </div>
        <div className="mx-auto mt-6 prose prose-lg text-gray-500 prose-clerk-blue">
          {tutorial.body ? (
            <SaneBlockChakra blocks={tutorial.body} />
          ) : (
            <SaneBlockChakra
              blocks={{ _type: 'markdown', markdown: tutorial.markdown }}
            />
          )}
        </div>
      </article>
      <div className="flex flex-row justify-center w-full my-10">
        <Link legacyBehavior href={tutorials}>
          <a className="z-10 flex items-center justify-center w-1/2 px-2 py-3 mx-2 mt-4 text-base font-medium text-white border border-transparent rounded-md shadow-md lg:w-1/6 bg-clerk-blue hover:bg-clerk-blue-hover md:py-4 md:text-lg whitespace-nowrap btn">
            More Tutorials
          </a>
        </Link>
      </div>
      <PreviewBadge isLive={tutorial.live} />
    </div>
  );
}

export async function getStaticProps({ params, preview = false }: any) {
  const { props } = await singlePageProps({
    preview,
    slug: '/tutorials'
  });

  const defaultSeo = props.seo || {};

  const { tutorial, moreTutorials } = await getClient(preview).fetch(
    tutorialQuery,
    {
      slug: '/tutorials/' + params.slug
    }
  );

  return {
    props: {
      seo: {
        ...defaultSeo,
        title: tutorial.title,
        ogImage: tutorial.coverImage,
        description: tutorial.excerpt
      },
      preview,
      data: {
        tutorial,
        moreTutorials: overlayDrafts(moreTutorials)
      }
    }
  };
}

export async function getStaticPaths() {
  const paths = await sanityClient.fetch(
    isProduction ? tutorialSlugsQueryProduction : tutorialSlugsQueryPreview
  );

  return {
    paths: paths.map((slug: string) => ({
      params: { slug: slug.replace('/tutorials/', '') }
    })),
    fallback: true
  };
}
